import { storydocThemes } from "@/pages/editor/modules/design-system/modules/theme/data/storydoc-themes";
import type { TDesignSystem } from "@/shared/types";

type DesignSystemV2 = Record<string, unknown>;

export type ResolvedDesignSystem = {
  designSystemV2: DesignSystemV2;
  // Only set when a selected account design system carries a custom-color
  // palette — it travels with the design system onto the story.
  customColors?: string[];
};

// Color tokens we override when theming from a preset / PDF palette (those
// sources carry colors only). Account themes bring their FULL design system.
const COLOR_KEYS = [
  "primaryColor",
  "secondaryColor",
  "themeColor3",
  "themeColor4",
  "backgroundColor",
  "backgroundColor2",
] as const;

const pickColors = (src: Record<string, unknown>): DesignSystemV2 => {
  const out: DesignSystemV2 = {};
  for (const key of COLOR_KEYS) {
    const value = src[key];
    if (typeof value === "string" && value) out[key] = value;
  }
  return out;
};

/**
 * Resolve the design system to apply to a File-to-Storydoc generated story.
 *
 * Priority:
 *  1. A selected account design system → its WHOLE design system (colors,
 *     fonts, sizes, everything) over base + its `customColors` palette.
 *  2. A selected Storydoc preset → the preset's colors over base (presets carry
 *     colors only).
 *  3. No selection → the PDF-derived `pdfDesignColors` palette over base.
 *  4. Nothing usable → the base unchanged.
 *
 * Everything merges over `base` so the result is always a complete token set.
 */
export const resolveDesignSystem = ({
  base,
  selectedDesignSystemId,
  themes,
  pdfDesignColors,
}: {
  base: DesignSystemV2;
  selectedDesignSystemId: string | null;
  themes: TDesignSystem[] | undefined;
  pdfDesignColors?: Record<string, string> | null;
}): ResolvedDesignSystem => {
  if (selectedDesignSystemId) {
    const accountTheme = themes?.find((t) => t._id === selectedDesignSystemId);
    if (accountTheme?.designSystem) {
      return {
        designSystemV2: { ...base, ...accountTheme.designSystem },
        customColors: accountTheme.customColors,
      };
    }
    const preset = storydocThemes.find((p) => p.id === selectedDesignSystemId);
    if (preset) {
      return {
        designSystemV2: {
          ...base,
          ...pickColors(preset as unknown as Record<string, unknown>),
        },
      };
    }
  }

  if (pdfDesignColors && Object.keys(pdfDesignColors).length > 0) {
    return { designSystemV2: { ...base, ...pdfDesignColors } };
  }

  return { designSystemV2: base };
};
